import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ShieldCheck, Mail, Lock, User, UserPlus, KeyRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Field, GoogleIcon, Coinciden } from "@/pages/Login";

const googleLogin = (codigo) => {
  // El código de invitación se guarda para enviarlo al volver de Google.
  if (codigo.trim()) sessionStorage.setItem("aldia-codigo-invitacion", codigo.trim());
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [nombre, setNombre] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [repite, setRepite] = useState("");
  const [codigo, setCodigo] = useState("");
  const [beta, setBeta] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/auth/beta").then((r) => setBeta(r.data.activa)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("La contraseña debe tener al menos 8 caracteres."); return; }
    if (password !== repite) { setError("Las contraseñas no coinciden."); return; }
    setBusy(true);
    try {
      await register(email, password, nombre, codigo.trim());
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-5 py-10">
      <div className="w-full max-w-md aldia-fade-up">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-[#3A6B56] flex items-center justify-center mx-auto">
            <ShieldCheck size={34} className="text-white" />
          </div>
          <p className="font-heading text-sm font-bold uppercase tracking-widest text-[#3A6B56] mt-4">AL DÍA</p>
          <h1 className="font-heading text-3xl font-bold text-[#1C2421] mt-1">Crea tu cuenta</h1>
          <p className="text-base text-[#6E7A74] mt-1">Empieza a tener tu documentación al día.</p>
        </div>

        <form onSubmit={submit} className="bg-white border border-[#E5DFD5] rounded-2xl p-6 shadow-sm space-y-4">
          <Field icon={User} label="Tu nombre" type="text" value={nombre} onChange={setNombre} placeholder="Ej. Ángel" testid="register-nombre" />
          <Field icon={Mail} label="Correo electrónico" type="email" value={email} onChange={setEmail} placeholder="tucorreo@ejemplo.com" testid="register-email" />
          <Field icon={Lock} label="Contraseña (mínimo 8 caracteres)" type="password" value={password} onChange={setPassword} placeholder="Elige una contraseña" testid="register-password" autoComplete="new-password" />
          <Field icon={Lock} label="Repetir contraseña" type="password" value={repite} onChange={setRepite} placeholder="Escríbela otra vez" testid="register-password-repite" autoComplete="new-password" invalido={!!repite && repite !== password} />
          <Coinciden a={password} b={repite} testid="register-coinciden" />
          {beta && <Field icon={KeyRound} label="Código de invitación" type="text" value={codigo} onChange={(v) => setCodigo(v.toUpperCase())} placeholder="ALDIA-XXXX-XXXX" testid="register-codigo" autoComplete="off" />}
          {error && <p data-testid="register-error" className="text-sm font-semibold text-[#B91C1C] bg-[#FEF2F2] rounded-xl p-3">{error}</p>}
          <button data-testid="register-submit" disabled={busy || !repite || repite !== password} className="w-full min-h-[56px] rounded-xl bg-[#3A6B56] text-white font-semibold text-lg hover:bg-[#2E5645] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            <UserPlus size={22} /> {busy ? "Creando…" : "Crear cuenta"}
          </button>

          <div className="flex items-center gap-3 py-1">
            <div className="flex-1 h-px bg-[#E5DFD5]" />
            <span className="text-sm text-[#9AA39D]">o</span>
            <div className="flex-1 h-px bg-[#E5DFD5]" />
          </div>

          <button type="button" data-testid="register-google" onClick={() => googleLogin(codigo)} className="w-full min-h-[56px] rounded-xl bg-white border-2 border-[#E5DFD5] text-[#1C2421] font-semibold text-base hover:border-[#3A6B56] transition-colors flex items-center justify-center gap-3">
            <GoogleIcon /> Continuar con Google
          </button>
        </form>

        <p className="text-center text-sm text-[#6E7A74] mt-5" data-testid="register-aviso-privacidad">
          Al crear tu cuenta puedes consultar cómo tratamos tus datos en nuestro{" "}
          <Link to="/privacidad" data-testid="register-link-privacidad" className="font-bold text-[#3A6B56] underline underline-offset-2">Aviso de Privacidad</Link>.
        </p>

        <p className="text-center text-base text-[#6E7A74] mt-6">
          ¿Ya tienes cuenta?{" "}
          <Link to="/login" data-testid="go-login" className="font-bold text-[#3A6B56]">Iniciar sesión</Link>
        </p>
      </div>
    </div>
  );
}
